import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart3,
  Eye,
  FileText,
  Loader2,
  PackageCheck,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  listAdminOrders,
  resetAdminOrderError,
  resetAdminOrderMeta,
} from "../../../actions/adminOrderActions";
import Meta from "../../../components/Meta";
import {
  formatDate,
  formatMoney,
  getCustomerName,
  getOrderCurrency,
  getOrderNumber,
  humanize,
  getFulfillmentStatusLabel,
  getFulfillmentStatusTone,
  getOrderStatusLabel,
  getOrderStatusTone,
  getPaymentStatusLabel,
  getPaymentStatusTone,
  safeText,
} from "../orderViewUtils";
import { getSafeUserMessage } from "../../../utils/errorMessages";

import "./AdminOrdersScreen.css";
import OrderRecoveryPanel from "./OrderRecoveryPanel";
import { hasFinancialPermission } from "./AdminOrderFinancialActions";

const PAGE_SIZE = 20;
const ORDER_STATUSES = [
  "",
  "pending_payment",
  "payment_action_required",
  "paid",
  "processing",
  "ready_to_ship",
  "ready_for_pickup",
  "shipped",
  "delivered",
  "fulfilled",
  "cancelled",
  "partially_refunded",
  "refund_pending",
  "refunded",
];
const PAYMENT_STATUSES = [
  "",
  "not_started",
  "requires_payment_method",
  "requires_action",
  "processing",
  "paid",
  "failed",
  "cancelled",
  "refund_pending",
  "partially_refunded",
  "refunded",
];

function StatusBadge({ value, fallback = "Pending", type = "order" }) {
  const label =
    type === "payment"
      ? getPaymentStatusLabel(value)
      : type === "fulfillment"
        ? getFulfillmentStatusLabel(value)
        : getOrderStatusLabel(value) || fallback;

  const tone =
    type === "payment"
      ? getPaymentStatusTone(value)
      : type === "fulfillment"
        ? getFulfillmentStatusTone(value)
        : getOrderStatusTone(value || fallback);

  return (
    <span className={`admin-orders__badge is-${tone}`}>
      {label}
    </span>
  );
}

function getPageParam(searchParams) {
  const page = Number(searchParams.get("page") || 1);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
}

function OrderRow({ order }) {
  const orderNumber = getOrderNumber(order);
  const currency = getOrderCurrency(order);
  const itemCount = Number(order.itemCount || 0);

  return (
    <tr>
      <td data-label="Order">
        <Link
          className="admin-orders__order-link"
          to={`/admin/orders/${encodeURIComponent(orderNumber)}`}
        >
          {orderNumber}
        </Link>
        <small>{formatDate(order.createdAt || order.placedAt)}</small>
      </td>
      <td data-label="Customer">
        <strong>{getCustomerName(order) || "Customer"}</strong>
        <small>{order.customer?.email || "No email"}</small>
      </td>
      <td data-label="Order status">
        <StatusBadge value={order.status} />
      </td>
      <td data-label="Payment">
        <StatusBadge
          value={order.payment?.status}
          fallback="Unpaid"
          type="payment"
        />
        <small>
          {humanize(order.payment?.provider || order.payment?.method, "Payment")}
        </small>
      </td>
      <td data-label="Fulfilment">
        <StatusBadge
          value={order.fulfillment?.status}
          fallback="Processing"
          type="fulfillment"
        />
        <small>{humanize(order.fulfillment?.method, "Delivery")}</small>
      </td>
      <td data-label="Total">
        <strong>{formatMoney(order.grandTotal, currency)}</strong>
        <small>{itemCount} item(s)</small>
      </td>
      <td data-label="Actions">
        <div className="admin-orders__actions">
          <Link
            className="admin-orders__ghost-btn"
            to={`/admin/orders/${encodeURIComponent(orderNumber)}`}
          >
            <Eye size={15} />
            <span>Manage</span>
          </Link>
          <Link
            className="admin-orders__ghost-btn"
            to={`/admin/orders/${encodeURIComponent(orderNumber)}/print`}
          >
            <FileText size={15} />
            <span>Print</span>
          </Link>
        </div>
      </td>
    </tr>
  );
}

export default function AdminOrdersScreen() {
  const dispatch = useDispatch();
  const financialPermission = useSelector((state) => hasFinancialPermission(state.userAuth?.userInfo || {}));
  const [searchParams, setSearchParams] = useSearchParams();
  const listState = useSelector((state) => state.adminOrder?.list || {});
  const orders = Array.isArray(listState.orders) ? listState.orders : [];

  const page = getPageParam(searchParams);
  const [orderNumber, setOrderNumber] = useState(
    searchParams.get("orderNumber") || "",
  );
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [paymentStatus, setPaymentStatus] = useState(
    searchParams.get("paymentStatus") || "",
  );
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [customer, setCustomer] = useState(searchParams.get("customer") || "");
  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState(searchParams.get("to") || "");
  const [attention, setAttention] = useState(searchParams.get("attention") === "true");

  const totalPages = Math.max(1, Number(listState.pages || 1));

  const query = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...Object.fromEntries(["search", "customer", "from", "to", "attention"].filter((key) => searchParams.get(key)).map((key) => [key, searchParams.get(key)])),
      ...(safeText(searchParams.get("orderNumber"))
        ? { orderNumber: safeText(searchParams.get("orderNumber")) }
        : {}),
      ...(safeText(searchParams.get("status"))
        ? { status: safeText(searchParams.get("status")) }
        : {}),
      ...(safeText(searchParams.get("paymentStatus"))
        ? { paymentStatus: safeText(searchParams.get("paymentStatus")) }
        : {}),
    }),
    [page, searchParams],
  );

  const loadOrders = useCallback(async () => {
    try {
      await dispatch(listAdminOrders(query));
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load orders right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, query]);

  useEffect(() => {
    loadOrders();

    return () => {
      dispatch(resetAdminOrderError());
      dispatch(resetAdminOrderMeta());
    };
  }, [dispatch, loadOrders]);

  const applyFilters = useCallback(() => {
    const next = new URLSearchParams();
    const cleanOrderNumber = safeText(orderNumber).toUpperCase();

    if (cleanOrderNumber) next.set("orderNumber", cleanOrderNumber);
    if (status) next.set("status", status);
    if (paymentStatus) next.set("paymentStatus", paymentStatus);
    for (const [key, value] of Object.entries({ search: search.trim(), customer: customer.trim(), from, to })) {
      if (value) next.set(key, value);
    }
    if (attention) next.set("attention", "true");
    if (searchParams.get("recovery") === "true") next.set("recovery", "true");
    next.set("page", "1");

    setSearchParams(next, { replace: true });
  }, [orderNumber, paymentStatus, setSearchParams, status, search, customer, from, to, attention, searchParams]);

  const setPage = useCallback(
    (nextPage) => {
      const next = new URLSearchParams(searchParams);
      next.set("page", String(Math.max(1, Math.min(totalPages, nextPage))));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, totalPages],
  );

  return (
    <main className="admin-orders">
      <Meta
        title="Admin orders | Academy Costumes London"
        description="Manage Academy Costumes London orders."
        robots="noindex, nofollow"
      />

      <header className="admin-orders__hero">
        <div>
          <p className="admin-orders__eyebrow">Operations</p>
          <h1>Orders</h1>
          <p className="admin-orders__subtitle">
            Review orders, payment state, fulfilment progress and print-ready
            documents from one operational view.
          </p>
        </div>

        <div className="admin-orders__hero-actions">
          <Link className="admin-orders__ghost-btn" to="/admin/orders/analytics">
            <BarChart3 size={16} />
            <span>Analytics</span>
          </Link>
          <button
            type="button"
            className="admin-orders__dark-btn"
            onClick={loadOrders}
            disabled={Boolean(listState.loading)}
          >
            {listState.loading ? (
              <Loader2 size={16} className="order-detail__spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {financialPermission ? <OrderRecoveryPanel /> : null}

      <section className="admin-orders__stats">
        <div className="admin-orders__stat-card">
          <span>Total</span>
          <strong>{Number(listState.count || orders.length)}</strong>
          <small>Matching orders</small>
        </div>
        <div className="admin-orders__stat-card">
          <span>Page</span>
          <strong>{page}</strong>
          <small>of {totalPages}</small>
        </div>
        <div className="admin-orders__stat-card">
          <span>Visible</span>
          <strong>{orders.length}</strong>
          <small>orders loaded</small>
        </div>
        <div className="admin-orders__stat-card">
          <span>Filters</span>
          <strong>
            {[orderNumber, status, paymentStatus, search, customer, from, to, attention ? "true" : ""].filter((item) => safeText(item)).length}
          </strong>
          <small>active</small>
        </div>
      </section>

      <section className="admin-orders__panel">
        <div className="admin-orders__toolbar">
          <label className="admin-orders__search">
            <Search size={18} />
            <input
              value={orderNumber}
              onChange={(event) => setOrderNumber(event.target.value)}
              placeholder="Order number"
              aria-label="Exact order number"
            />
          </label>

          <div className="admin-orders__filter-grid">
            <label><span>Search</span><input type="search" value={search} maxLength={120} onChange={(event) => setSearch(event.target.value)} placeholder="Order, item or customer" /></label>
            <label><span>Customer email</span><input type="search" value={customer} maxLength={120} onChange={(event) => setCustomer(event.target.value)} /></label>
            <label><span>From</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
            <label><span>To</span><input type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} /></label>
            <label><span>Needs attention</span><input type="checkbox" checked={attention} onChange={(event) => setAttention(event.target.checked)} /></label>
            <label>
              <span>Order</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {ORDER_STATUSES.map((item) => (
                  <option key={item || "all"} value={item}>
                    {item ? humanize(item) : "All statuses"}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Payment</span>
              <select
                value={paymentStatus}
                onChange={(event) => setPaymentStatus(event.target.value)}
              >
                {PAYMENT_STATUSES.map((item) => (
                  <option key={item || "all"} value={item}>
                    {item ? humanize(item) : "All payments"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="admin-orders__toolbar-actions">
            <button
              type="button"
              className="admin-orders__dark-btn"
              onClick={applyFilters}
            >
              Apply
            </button>
          </div>
        </div>

        {listState.loading && !orders.length ? (
          <div className="admin-orders__loader">
            <Loader2 className="order-detail__spin" />
          </div>
        ) : orders.length ? (
          <div className="admin-orders__table-wrap">
            <table className="admin-orders__table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Fulfilment</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <OrderRow key={order.id || order._id || order.orderNumber} order={order} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-orders__loader">
            <PackageCheck size={32} />
            <p>{listState.error || "No orders match these filters."}</p>
          </div>
        )}

        <footer className="admin-orders__pagination">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="admin-orders__pagination-actions">
            <button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1 || Boolean(listState.loading)}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages || Boolean(listState.loading)}
            >
              Next
            </button>
          </div>
        </footer>
      </section>
    </main>
  );
}
